namespace e5 {
    export interface ScrollbarData {
        scrollWidth?: number;
        scrollHeight?: number;
        offsetWidth?: number;
        clientHeight?: number;
        scrollLeft?: number;
        scrollTop?: number;
    }

    export type ScrollbarTheme = LiteralUnion<"lite" | "windows" | "mac" | "blue" | "mobile">;

    export class Scrollbar {
        public readonly element: HTMLDivElement;
        public readonly horizontal: boolean = false;
        public readonly buttonDecrement: HTMLDivElement;
        public readonly track: HTMLDivElement;
        public readonly trackPieceContainer: HTMLDivElement;
        public readonly trackPieceDecrement: HTMLDivElement;
        public readonly trackPieceIncrement: HTMLDivElement;
        public readonly thumb: HTMLDivElement;
        public readonly buttonIncrement: HTMLDivElement;
        public readonly dragger: Drag;

        protected _start: boolean = false;
        protected _end: boolean = false;

        constructor(horizontal: boolean = false, theme: ScrollbarTheme = "lite") {
            this.horizontal = horizontal;

            var orientation = horizontal ? "horizontal" : "vertical";

            this.element = document.createElement("div");
            this.element.className = `e5-scrollbar visible theme-${theme} ${orientation}`;
            this.element.innerHTML = `
            <div class="e5-scrollbar-button decrement ${orientation}"></div>
            <div class="e5-scrollbar-track ${orientation}">
                <div class="e5-scrollbar-track-piece-container ${orientation}">
                    <div class="e5-scrollbar-track-piece decrement ${orientation}"></div>
                    <div class="e5-scrollbar-track-piece increment ${orientation}"></div>
                </div>
                <div class="e5-scrollbar-thumb ${orientation}"><div class="e5-scrollbar-thumb-decoration ${orientation}"></div></div>
            </div>
            <div class="e5-scrollbar-button increment ${orientation}"></div>
            `;

            this.buttonDecrement = this.element.querySelector(".e5-scrollbar-button.decrement");
            this.track = this.element.querySelector(".e5-scrollbar-track");
            this.trackPieceContainer = this.element.querySelector(".e5-scrollbar-track-piece-container");
            this.trackPieceDecrement = this.element.querySelector(".e5-scrollbar-track-piece.decrement");
            this.trackPieceIncrement = this.element.querySelector(".e5-scrollbar-track-piece.increment");
            this.thumb = this.element.querySelector(".e5-scrollbar-thumb");
            this.buttonIncrement = this.element.querySelector(".e5-scrollbar-button.increment");

            this.dragger = new Drag(this.thumb);
            this.dragger.clampTrigger = Trigger.MOVE | Trigger.START;
            this.dragger.clamp = { parent: this.track };
            this.dragger.onDown.add(() => this.dragStart());
            
            // var slot = this.dragger.onDrag.throttle(() => this.drag(), 100);
            // this.dragger.onClear.add(() => {
            //     // slot.immediate();
            //     slot.cancel();
            //     this.dragEnd();
            // });

            this.dragger.onDrag.add(() => this.drag());
            this.dragger.onClear.add(() => {
                this.dragEnd();
            });

            this.dragger.dragX = this.horizontal;
            this.dragger.dragY = !this.horizontal;

            var actor = new Actor(this.trackPieceContainer, { maxPointers: 1 });
            actor.onDown.add(() => {
                if (this.dragger.pointers.length > 0)
                    return;
                var local = Transform.toLocal(this.track, actor.pointer.x, actor.pointer.y);
                var barPosition = local.y - this.thumb.offsetHeight * 0.5;
                this.thumb.style.transform = `translateY(${barPosition}px)`;
                this.dragger.start(actor.pointer.event);
                this.updateState();
            });

            var clickDecrement = new Click(this.buttonDecrement);
            clickDecrement.onRepeat.add((count) => {
                this.decrement(count);
            });

            var clickIncrement = new Click(this.buttonIncrement);
            clickIncrement.onRepeat.add((count) => {
                this.increment(count);
            });
        }

        protected increment(count: number): void {

        }

        protected decrement(count: number): void {

        }

        protected dragStart(): void {
            this.active = true;
        }

        protected drag(): void {
            this.updateState();
        }

        protected dragEnd(): void {
            this.active = false;
            this.update();
        }

        protected updateState(): void {
            var start: boolean;
            var end: boolean;
            if (this.horizontal) {
                var left = this.dragger.left;
                var right = this.dragger.right;
                start = left <= 0;
                end = right >= this.track.offsetWidth;
                this.trackPieceDecrement.style.height = left + "px";
                this.trackPieceIncrement.style.height = (this.track.offsetWidth - right) + "px";
            }
            else {
                var top = this.dragger.top;
                var bottom = this.dragger.bottom;
                start = top <= 0;
                end = bottom >= this.track.offsetHeight;
                this.trackPieceDecrement.style.height = top + "px";
                this.trackPieceIncrement.style.height = (this.track.offsetHeight - bottom) + "px";
            }

            this.start = start;
            this.end = end;
        }

        protected get start(): boolean {
            return this._start;
        }

        protected set start(start: boolean) {
            if (this._start === start)
                return;
            this._start = start;
            this.buttonIncrement.classList.toggle("start", start);
            this.buttonDecrement.classList.toggle("start", start);
            this.trackPieceContainer.classList.toggle("start", start);
            this.trackPieceDecrement.classList.toggle("start", start);
            this.trackPieceIncrement.classList.toggle("start", start);
        }

        protected get end(): boolean {
            return this._end;
        }

        protected set end(end: boolean) {
            if (this._end === end)
                return;
            this._end = end;
            this.buttonIncrement.classList.toggle("end", end);
            this.buttonDecrement.classList.toggle("end", end);
            this.trackPieceContainer.classList.toggle("end", end);
            this.trackPieceDecrement.classList.toggle("end", end);
            this.trackPieceIncrement.classList.toggle("end", end);
        }

        protected get active(): boolean {
            return this.element.classList.contains("active");
        }

        protected set active(active: boolean) {
            this.element.classList.toggle("active", active);
            this.thumb.classList.toggle("active", active);
        }

        protected getPosition(): ScrollbarData {
            return null;
        }

        public update(data: ScrollbarData = null): void {
            if (this.dragger.pointers.length > 0)
                return;

            data = data || this.getPosition();
            if (!data)
                return;

            // var visible = this._horizontal ? Math.floor(data.contentWidth) > data.width : Math.floor(data.contentHeight) > data.height;
            // this.element.classList.toggle("visible", visible);
            // if (!visible)
            //     return;

            var minBarSize = 50;
            var minBarElastic = 25;

            var contentSize: number;
            var viewSize: number;
            var trackSize: number;
            var contentPosition: number;
            if (this.horizontal) {
                contentSize = data.scrollWidth;
                viewSize = data.offsetWidth;
                trackSize = this.track.offsetWidth;
                contentPosition = -data.scrollLeft;
            }
            else {
                contentSize = data.scrollHeight;
                viewSize = data.clientHeight;
                trackSize = this.track.offsetHeight;
                contentPosition = -data.scrollTop;
            }

            var barSize = Calc.clamp((viewSize / contentSize) * trackSize, minBarSize, trackSize);
            var min = viewSize - contentSize;
            var max = 0;
            var barPosition = 1 - ((contentPosition - min) / (max - min));
            barPosition *= trackSize - barSize;
            if (barPosition < 0) {
                barSize = Calc.clampMin(barSize + barPosition, minBarElastic);
                barPosition = 0;
            } else if (barPosition + barSize > trackSize) {
                barSize = Calc.clampMin(trackSize - barPosition, minBarElastic);
                barPosition = trackSize - barSize;
            }

            barSize = Math.round(barSize);
            // barPosition = Math.round(barPosition);

            if (this.horizontal) {
                this.thumb.style.width = barSize + "px";
                this.thumb.style.transform = `translateX(${barPosition}px)`;
            }
            else {
                this.thumb.style.height = barSize + "px";
                this.thumb.style.transform = `translateY(${barPosition}px)`;
            }

            this.updateState();
        }
    }
}